import React, { useState, useEffect, ChangeEvent } from "react";
import classnames from "classnames";
import _, { set, update } from "lodash";
import moment from "moment";
import {
  QualityAlert,
  QualityAlertSupplierDirective,
} from "../../../models/quality-alert";
import ReactModal from "react-modal";
const colors = {
  cardColor0: "#76a7fa",
  cardColor1: "#e46f61",
  cardColor2: "#53a93f",
  cardColor3: "#fbcb43",
};

const fromUTCString = (utcString: string | null, toJsDate: boolean = false) => {
  if (!utcString) return;
  const m = moment.utc(utcString, "YYYY-MM-DD HH:mm:ss");
  if (m.isValid()) {
    return toJsDate ? m.local().toDate() : m.local();
  }
};

interface SupplierDirectiveBoxProps {
  qualityAlertId?: number | "new";
  qualityAlert: QualityAlert | null;
  supplierDirectives?: Array<{
    createUser?: {
      firstName?: string;
      lastName?: string;
    };
    createTime?: string;
    desc?: string;
    id?: number;
  }>;
  sortRequired?: boolean;
  materialRequested?: boolean;
  onCheck: (field: string, event: { target: { value: boolean } }) => void;
  className?: string;
  updateQualityAlert: (qualityAlert: QualityAlert) => void;
}

const SupplierDirectiveBox: React.FC<SupplierDirectiveBoxProps> = ({
  qualityAlertId,
  qualityAlert,
  onCheck,
  className,
  updateQualityAlert,
}) => {
  const [isModalOpen, setModalIsOpen] = useState(false);
  const [supplierDirectives, setSupplierDirectives] = useState<any[]>([]);
  const [supplierDirective, setSupplierDirective] =
    useState<QualityAlertSupplierDirective>(
      {} as QualityAlertSupplierDirective,
    );
  const [desc, setDesc] = useState<string>("");
  const [modalHeader, setModalHeader] = useState<string>(
    "New Supplier Directive",
  );
  const [saveButtonText, setSaveButtonText] = useState<string>("Add");
  const [supplierDirectiveIndex, setSupplierDirectiveIndex] = useState<
    number | "new"
  >("new");
  const renderDirectives = () => {
    if (!qualityAlert) return null;

    const data = qualityAlert.supplierDirectives || [];
    return data.map((directive, i) => {
      let title = localStorage.getItem("user_name") || "";
      if (directive.createTime) {
        //TODO: uncomment this line
        //title += ` - ${fromUTCString(directive.createTime)?.format("MM/DD/YYYY h:mm A")}`;
      } else {
        title += ` - ${moment().format("MM/DD/YYYY h:mm A")}`;
      }

      const lines = (directive.desc || "")
        .split("\n")
        .map((line: any, j: any) => <div key={`${j}line`}>{line}</div>);

      return (
        <div key={`${i}directive`} className="mb-2.5">
          <div>
            <b>{title}</b>
            {directive.id == null && (
              <span
                onClick={() => onClickSupplierDirective(i)}
                className="ml-2.5 cursor-pointer text-blue-400"
              >
                {"Edit"}
              </span>
            )}
          </div>
          <div className="ml-1.5 mt-0.5">{lines}</div>
        </div>
      );
    });
  };

  const onAppend = () => {
    setModalIsOpen(!isModalOpen);
  };

  // TODO implement this
  const onClickSupplierDirective = (index: number) => {
    setModalHeader("Edit Supplier Directive");
    setModalIsOpen(!isModalOpen);
    // set description here
    let supplierDirective = qualityAlert?.supplierDirectives[index].desc;
    if (supplierDirective) {
      setDesc(supplierDirective);
      setSupplierDirective(
        qualityAlert?.supplierDirectives[index] ||
          ({} as QualityAlertSupplierDirective),
      );
      setSupplierDirectiveIndex(index);
      setSaveButtonText("Save");
    }
  };

  const addSupplierDirective = (
    supplierDirective: QualityAlertSupplierDirective,
  ) => {
    if (!qualityAlert || !supplierDirective.desc) return;
    const directives = qualityAlert?.supplierDirectives || [];
    if (supplierDirective.id == null) {
      directives.push(supplierDirective);
    } else {
      directives[supplierDirective.id] = supplierDirective;
    }
    qualityAlert.supplierDirectives = directives;
    updateQualityAlert({ ...qualityAlert });
  };

  const updateSupplierDirective = (
    index: number | "new",
    supplierDirective: QualityAlertSupplierDirective,
  ) => {
    if (!qualityAlert || !supplierDirective.desc) return;
    const directives = qualityAlert?.supplierDirectives || [];
    if (index !== "new") {
      directives[index] = supplierDirective;
    }
    qualityAlert.supplierDirectives = directives;
    updateQualityAlert({ ...qualityAlert });
  };

  const saveDescription = (e: any) => {
    setModalIsOpen(!isModalOpen);

    if (supplierDirectiveIndex === "new") {
      addSupplierDirective(supplierDirective);
    } else {
      updateSupplierDirective(supplierDirectiveIndex, supplierDirective);
    }
  };

  const textAreaChanged = (value: string) => {
    setDesc(value);
    setSupplierDirective({ ...supplierDirective, desc: value });
  };

  const getDescriptionText = () => {
    if (supplierDirective.id == null) {
      return desc;
    }
    return supplierDirective.desc;
  };

  const onCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!qualityAlert) {
      return;
    }
    qualityAlert = { ...qualityAlert, [event.target.id]: event.target.checked };
    updateQualityAlert({ ...qualityAlert });
  };

  return (
    <div className={classnames(className, "flex flex-col items-stretch p-2")}>
      <div className="mb-1.5 text-sm italic">
        {"What actions does the OEM require for the Supplier?"}
      </div>

      <div className="ms-1 inline-flex items-center">
        <label className="relative flex cursor-pointer items-center text-black">
          <input
            type="checkbox"
            className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-black shadow transition-all checked:border-blue-600 checked:bg-blue-600 hover:shadow-md"
            id="sortRequired"
            checked={qualityAlert?.sortRequired || false}
            onChange={onCheckboxChange}
          />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform text-white opacity-0 peer-checked:opacity-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3.5 w-3.5"
              viewBox="0 0 20 20"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="1"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        </label>
        <span className="ms-2 text-[15px] text-black">
          Sort Required at OEM
        </span>
      </div>

      <div className="ms-1 inline-flex items-center">
        <label className="relative flex cursor-pointer items-center text-black">
          <input
            type="checkbox"
            className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-black shadow transition-all checked:border-blue-600 checked:bg-blue-600 hover:shadow-md"
            id="materialRequested"
            checked={qualityAlert?.materialRequested || false}
            onChange={onCheckboxChange}
          />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform text-white opacity-0 peer-checked:opacity-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3.5 w-3.5"
              viewBox="0 0 20 20"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="1"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        </label>
        <span className="ms-2 text-[15px] text-black">
          Certified Material Required
        </span>
      </div>

      <div className="flex justify-start">
        <div
          className="mb-1.5 w-[70px] cursor-pointer rounded bg-green-600 p-1.5 text-center text-sm text-white hover:bg-gray-400"
          onClick={onAppend}
        >
          {"+ Action"}
        </div>
      </div>

      <div className="max-h-[150px] min-h-[70px] overflow-auto border border-gray-300 bg-transparent text-sm text-gray-600">
        <div className="p-1.5">{renderDirectives()}</div>
      </div>
      <ReactModal
        isOpen={isModalOpen}
        onRequestClose={() => setModalIsOpen(!isModalOpen)}
        className="relative w-96 rounded bg-white p-6 shadow-md"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
      >
        <h2 className="mb-4 align-middle text-lg font-bold text-black">
          {modalHeader}
        </h2>
        <textarea
          className="h-40 w-full border border-gray-300 bg-white p-2 text-black"
          placeholder="Add a description..."
          value={desc}
          onChange={(evt) => textAreaChanged(evt.target.value)}
        />
        <div className="flex justify-end">
          <div
            className="mt-2 cursor-pointer rounded bg-green-600 p-1.5 text-center text-sm text-white hover:bg-gray-400"
            onClick={saveDescription}
          >
            {saveButtonText}
          </div>
        </div>
      </ReactModal>
    </div>
  );
};

export default SupplierDirectiveBox;
